import publicApiClient from '../publicApiClient';

/**
 * Public Customer Service
 * Used for fetching customer details in public/token-based flows
 */
export const publicCustomerService = {
    /**
     * Get customer details by public ID
     * This endpoint requires authentication but is called in the context of a validated order token
     * 
     * @param {string} customerPublicId - Customer's public ID
     * @param {string} supplierId - Supplier's Public ID (Tenant Context)
     * @returns {Promise<Object>} Customer details
     */
    async getCustomerById(customerPublicId, supplierId) {
        try {
            console.log(`[PublicCustomerService] Fetching customer: ${customerPublicId}, supplier: ${supplierId}`);

            if (!supplierId) {
                console.error('[PublicCustomerService] Missing supplierId');
                throw new Error('Supplier ID is required to fetch customer details');
            }

            const response = await publicApiClient.get(`/public/customers/${customerPublicId}`, {
                params: { supplierId }
            });
            console.log('[PublicCustomerService] Customer fetched successfully:', response.data);

            // Transform backend CustomerDTO to frontend format
            return {
                id: response.data.publicId,
                customerId: response.data.customerId,
                name: response.data.name || response.data.customerName || '',
                whatsappNumber: response.data.whatsappNumber,
                contactPerson: response.data.contactPerson,
                designation: response.data.designation,
                mobile: response.data.mobileNumber || response.data.mobile || '',
                email: response.data.email || '',
                trn: response.data.trn || '',
                address: response.data.address,
                categoryId: response.data.categoryPublicId,
                categoryName: response.data.categoryName,
                countryId: response.data.countryPublicId,
                countryName: response.data.countryName,
                stateId: response.data.statePublicId,
                stateName: response.data.stateName,
                salesPersons: response.data.salesPersons,
                preferredTimeSlot: response.data.preferredTimeSlot || '',
                isTrading: response.data.isTrading !== false && response.data.trading !== false, // Defaults to true if not defined
                createdAt: response.data.createdAt
            };
        } catch (error) {
            console.error('[PublicCustomerService] Error fetching customer:', error);

            // Handle specific error cases
            if (error.response?.status === 404) {
                throw new Error('Customer not found');
            } else if (error.response?.status === 401 || error.response?.status === 403) {
                throw new Error('Unauthorized access to customer data');
            }

            throw error;
        }
    },

    /**
     * Update customer profile details (address and preferred schedule)
     * Requires the customer's portal JWT token
     */
    async updateCustomerProfile(token, profileData) {
        try {
            console.log('[PublicCustomerService] Updating customer profile details');
            const response = await publicApiClient.put('/customer/profile', profileData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            console.log('[PublicCustomerService] Profile updated successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[PublicCustomerService] Error updating customer profile:', error);
            throw error;
        }
    }
};
